/**
 * useSignedTokenSync Hook
 *
 * Fetches and stores signed entitlement tokens from the server.
 * Tokens are used for offline Pro verification with cryptographic signature.
 *
 * This hook syncs signed tokens:
 * - On sign-in (when user becomes authenticated)
 * - After subscription refresh (when subscription state changes)
 * - On app visibility change (returning from Stripe checkout)
 *
 * The signed token is stored via IPC and used by the main process
 * for offline entitlement decisions.
 */

import { useEffect, useRef, useCallback } from 'react';
import { useAuthStore } from '../store/authStore';
import { useLicenseStore } from '../store/licenseStore';
import { useSubscriptionStore } from '../store/subscriptionStore';
import { fetchEntitlementToken, isEntitlementTokenServiceConfigured } from '../services/entitlementTokenService';
import { onAuthStateChange } from '../services/authService';
import { shouldThrottleVisibilityRefresh } from './visibilityRefreshThrottle';

/**
 * Store a signed entitlement token via IPC.
 */
async function storeSignedToken(token: string): Promise<boolean> {
  try {
    const api = window.electronAPI?.licenseCache;
    if (!api?.updateCacheWithSignedToken) {
      console.warn('[SignedTokenSync] IPC not available');
      return false;
    }
    return await api.updateCacheWithSignedToken(token);
  } catch (error) {
    console.error('[SignedTokenSync] Failed to store token:', error);
    return false;
  }
}

/**
 * Fetch and store a fresh signed entitlement token.
 */
async function refreshSignedToken(): Promise<boolean> {
  if (!isEntitlementTokenServiceConfigured()) {
    console.log('[SignedTokenSync] API not configured, skipping token fetch');
    return false;
  }

  const result = await fetchEntitlementToken();

  if (!result.success || !result.token) {
    console.warn('[SignedTokenSync] Failed to fetch token:', result.error);
    return false;
  }

  // Keep the Zustand trial dates in sync with the server. Without this,
  // server-side changes (e.g. admin-extended trial_expires_at) only land
  // in the signed token cache, so the UI countdown stays stale until the
  // user signs out and in again.
  const payload = result.payload;
  if (payload?.mode === 'trial' && payload.trial_started_at && payload.trial_expires_at) {
    const userId = useAuthStore.getState().user?.id;
    useLicenseStore.getState().startTrialFromServer(
      new Date(payload.trial_started_at),
      new Date(payload.trial_expires_at),
      userId
    );
  }

  const stored = await storeSignedToken(result.token);
  if (stored) {
    console.log('[SignedTokenSync] Token stored successfully, mode:', result.payload?.mode);
  }
  return stored;
}

/**
 * Hook that fetches and stores signed entitlement tokens.
 * Should be used in a top-level component (e.g., App.tsx).
 */
export function useSignedTokenSync(): void {
  const userId = useAuthStore((s) => s.user?.id ?? null);
  const isAuthInitialized = useAuthStore((s) => s.isInitialized);

  const subscription = useSubscriptionStore((s) => s.subscription);
  const subscriptionStatus = subscription?.status ?? 'none';
  const subscriptionLoading = useSubscriptionStore((s) => s.isLoading);

  const lastUserIdRef = useRef<string | null>(null);
  const lastSubscriptionStatusRef = useRef<string | null>(null);
  const isRefreshingRef = useRef(false);
  // epoch ms of the last completed refresh (any trigger); used only to
  // throttle the visibility trigger (see visibilityRefreshThrottle.ts).
  const lastCompletedRefreshAtRef = useRef<number | null>(null);

  // Debounced refresh to avoid multiple concurrent fetches.
  // A watchdog clears the in-flight guard if a refresh ever hangs (IPC stall),
  // so a single stuck call can't permanently block all future refreshes.
  const debouncedRefresh = useCallback(async () => {
    if (isRefreshingRef.current) {
      return;
    }
    isRefreshingRef.current = true;
    const watchdog = setTimeout(() => {
      isRefreshingRef.current = false;
    }, 30_000);
    try {
      await refreshSignedToken();
      lastCompletedRefreshAtRef.current = Date.now();
    } finally {
      clearTimeout(watchdog);
      isRefreshingRef.current = false;
    }
  }, []);

  // Sync when user signs in or subscription changes
  useEffect(() => {
    if (!isAuthInitialized) return;

    // User signed out - nothing to sync
    if (!userId) {
      lastUserIdRef.current = null;
      lastSubscriptionStatusRef.current = null;
      return;
    }

    // Don't refresh while subscription is loading
    if (subscriptionLoading) return;

    // Check if we need to refresh
    const userChanged = lastUserIdRef.current !== userId;
    const subscriptionChanged = lastSubscriptionStatusRef.current !== subscriptionStatus;

    if (userChanged || subscriptionChanged) {
      lastUserIdRef.current = userId;
      lastSubscriptionStatusRef.current = subscriptionStatus;
      void debouncedRefresh();
    }
  }, [userId, isAuthInitialized, subscriptionStatus, subscriptionLoading, debouncedRefresh]);

  // Also refresh on auth state changes and visibility
  useEffect(() => {
    if (!isAuthInitialized || !userId) return;

    // Refresh on auth events (token refresh, sign-in); flag a stale session so
    // the UI shows a clear re-logon prompt when the main process reports the
    // OIDC session can no longer be refreshed.
    const unsubscribeAuth = onAuthStateChange((event) => {
      if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') {
        void debouncedRefresh();
      } else if (event === 'AUTH_STALE') {
        useSubscriptionStore.getState().markAuthStale(true);
      }
    });

    // Refresh on visibility change (returning from Stripe checkout).
    // Throttled: this fires on every alt-tab back into the app, not just
    // returning from checkout, so without a floor it dominates refresh
    // volume. 10 minutes comfortably covers the checkout round-trip while
    // cutting the "focus the window" spam (see visibilityRefreshThrottle.ts).
    const handleVisibilityChange = () => {
      if (document.visibilityState !== 'visible') return;
      if (shouldThrottleVisibilityRefresh(lastCompletedRefreshAtRef.current)) return;
      void debouncedRefresh();
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      unsubscribeAuth();
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [isAuthInitialized, userId, debouncedRefresh]);

  // Proactive renewal now lives in the MAIN process (sessionHealthScheduler):
  // it refreshes the OIDC token on launch/resume/focus + a 6h backstop and
  // drives this hook via TOKEN_REFRESHED (re-mint) / AUTH_STALE (re-logon).
  // That survives window-hide/sleep, which a renderer timer could not.
}

/**
 * Manually trigger a signed token refresh.
 * Useful for testing or manual verification.
 */
export async function refreshEntitlementToken(): Promise<boolean> {
  return refreshSignedToken();
}
